import isNil from 'lodash/isNil';
import {AttrsSchema, Component} from 'panel';

import {useEventListener} from 'common/util/component-helpers';
import {formatDate, parseDate} from 'common/util/date';
import {formatMaxHistoryDays} from 'common/report/util';
import {filterAttributes, parseNumber} from 'common/util';
import {NAVIGATION_KEY_CODES, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import type {CalendarInput} from 'common/widgets/date-range-picker/calendar-input';

import {DateRange, DateRangeAttributes} from './models/date-range';
import {DateRangeFactory} from './models/date-range-factory';
import {DATE_RANGE_TYPE_DISPLAY_NAMES, TIME_UNIT_DISPLAY_NAMES} from './models/presets';
import {DateRangeType, DisplayContext, RelativeDate} from './models/types';
import {getDateLimitedTooltipText, getMinDateStr} from './utils';
import {template} from './template';
import css from './index.inline.styl';

/**
 * TODO: @jlack -- write tests for component before adding to mixpanel-common
 */

type Attrs = {
  'date-range': DateRangeAttributes;
  'display-context': DisplayContext;
  'hide-hours': boolean;
  'max-date': string;
  'show-max-date-past-context': boolean;
  'max-history-days': string;
  'show-upsell': string;
};

type State = {
  dateRange: DateRange;
  isRangeInputMousedOver: boolean;
  isRangeInputFocused: boolean;
};

export class DateRangePicker extends Component<State, Attrs, unknown, unknown, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<Attrs> {
    return {
      ...super.attrsSchema,
      'date-range': {type: `json`, description: `interface: DateRange`},
      'display-context': {type: `string`},
      'hide-hours': {type: `boolean`},
      'max-date': {type: `string`},
      'show-max-date-past-context': {
        type: `boolean`,
        description: `shows context for projects with max date in the past`,
      },
      'max-history-days': {type: `string`},
      'show-upsell': {type: `string`},
    };
  }

  attributeChangedCallback(attr: keyof Attrs, oldValue: any, newValue: any) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    switch (attr) {
      case `date-range`:
      case `display-context`: {
        this.updateDateRange();
        requestAnimationFrame(() => this.dispatchEvent(new CustomEvent(`draw`)));
        break;
      }
    }
  }

  updateDateRange() {
    const dateRangeAttrs = this.attr(`date-range`) || {};
    const displayContext = this.getDisplayContext();
    this.update({
      dateRange: DateRangeFactory.createDateRange(dateRangeAttrs, {displayContext}),
    });
  }

  connectedCallback() {
    this.updateDateRange();
    super.connectedCallback();
    useEventListener(this, window, `keydown`, (ev: Event) => {
      if ((ev as KeyboardEvent).keyCode === NAVIGATION_KEY_CODES.ENTER) {
        this.dispatchEvent(new CustomEvent(`confirmDateRange`));
      }
    });
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
      contexts: [`i18n` as const],
      defaultState: {
        // todo: this technically can be null but none of the code in here accounts for it (it werk tho)
        // this should be computed anyways and not in state
        dateRange: null as any,
        isRangeInputMousedOver: false,
        isRangeInputFocused: false,
      },
      helpers: {
        ...super.config.helpers,
        filterAttributes,
        DateRangeType,
        DATE_RANGE_TYPE_DISPLAY_NAMES,
        TIME_UNIT_DISPLAY_NAMES,
        propagateEvent: (ev: any) => {
          propagateEvent(this, ev);
        },
        isValidDateRange: () => {
          return this.state.dateRange.isValid();
        },
        isWithinMaxHistoryLimit: () => {
          const maxHistoryDays = this.helpers.maxHistoryDays();
          if (Number.isInteger(maxHistoryDays)) {
            return this.state.dateRange.isWithinMaxHistoryLimit(maxHistoryDays!);
          }
          return true;
        },
        changedDateRangeType: (ev: any) => {
          const {dateRange} = this.state;
          const type = dateRange.getDateRangeTypeOptions().find((dateRangeType) => {
            return DATE_RANGE_TYPE_DISPLAY_NAMES[dateRangeType] === ev.detail.value;
          });
          const updates: any = {type};
          // When switching from "Since" to "On" without changing the selected date,
          // we need to make sure "to" is updated from null
          if (type === DateRangeType.On || type === DateRangeType.StartsOn || type === DateRangeType.EndsOn) {
            updates.to = dateRange.from;
          }
          if (type === DateRangeType.Before) {
            updates.to = dateRange.from;
            updates.from = null;
          }
          // changing from a rolling "between" or "on", set "from" from relative to absolute
          if (type === DateRangeType.Since && dateRange.rolling && DateRange.isRelativeDate(dateRange.from!)) {
            updates.from = DateRange.convertRelativeDateToCalendarDate(dateRange.from);
          }
          this.dispatchChangeDateRangeEvent(updates);
        },
        changedDates: (ev: any) => {
          const {rolling, type} = this.state.dateRange;
          let {from, to} = ev.detail;
          if (type === DateRangeType.On || type === DateRangeType.StartsOn || type === DateRangeType.EndsOn) {
            to = from;
          } else if (type === DateRangeType.Before) {
            to = from;
            from = null;
          } else if (type === DateRangeType.Since) {
            to = null;
          }
          if (rolling) {
            [from, to] = [
              DateRange.convertCalendarDateToRelativeDate(from),
              DateRange.convertCalendarDateToRelativeDate(to),
            ];
          }
          this.dispatchChangeDateRangeEvent({from, to});
        },
        mouseenterRangeInput: () => {
          this.update({isRangeInputMousedOver: true});
        },
        mouseleaveRangeInput: () => {
          this.update({isRangeInputMousedOver: false});
        },
        blurredRangeInput: () => {
          this.update({isRangeInputFocused: false});
        },
        focusedRangeInput: () => {
          this.update({isRangeInputFocused: true});
        },
        changedRelativeRangeValue: (ev: any) => {
          const value = parseNumber(ev.target.value);
          this.dispatchChangeDateRangeEvent({window: {value} as any});
        },
        changedRelativeRangeUnit: (ev: any) => {
          const {dateRange} = this.state;
          const unit = dateRange
            .getTimeUnitOptions()
            .find((timeUnit) => TIME_UNIT_DISPLAY_NAMES[timeUnit] === ev.detail.value);
          this.dispatchChangeDateRangeEvent({window: {unit} as any});
          this.selectRelativeInput();
        },
        handleInsertCalendarInput: (vnode: any) => {
          switch (this.state.dateRange.type) {
            case DateRangeType.On:
            case DateRangeType.Since:
              requestAnimationFrame(() => vnode.elm.focusFromInput());
              break;
            default:
              break;
          }
        },
        minDate: () => {
          return getMinDateStr({maxHistoryDays: this.helpers.maxHistoryDays()});
        },
        maxHistoryDays: () => {
          return this.getNumberAttribute(`max-history-days`);
        },
        maxHistoryDaysText: () => {
          return formatMaxHistoryDays(this.helpers.maxHistoryDays());
        },
        dateLimitedTooltipText: () => {
          return getDateLimitedTooltipText({
            maxHistoryDays: this.helpers.maxHistoryDays(),
            showUpsell: this.helpers.showUpsell(),
            beforeEpoch: isNil(this.helpers.minDate()),
          });
        },
        showMaxDateContext: () => {
          return (
            !!this.attr(`show-max-date-past-context`) &&
            !!this.attr(`max-date`) &&
            [DateRangeType.RelativeAfter, DateRangeType.Since].includes(this.state.dateRange.type)
          );
        },
        overrideMaxDateContextZIndex: () => {
          // Override z index for since to make it show above the calendar.
          return [DateRangeType.Since].includes(this.state.dateRange.type);
        },
        maxDateContextText: () => {
          return `Ending ${this.getMaxDateLabel()}`;
        },
        maxDateTooltipText: () => {
          return `Project contains data through ${this.getMaxDateLabel()}`;
        },
        isRangeInputActive: () => {
          return this.state.isRangeInputFocused || this.state.isRangeInputMousedOver;
        },
        showUpsell: () => {
          return this.isAttributeEnabled(`show-upsell`) && Number.isInteger(this.helpers.maxHistoryDays());
        },
        clickedRollingCheckbox: () => {
          const {dateRange} = this.state;
          let {from, to} = dateRange;
          const rolling = !dateRange.rolling;
          if (rolling) {
            [from, to] = [
              from && DateRange.convertCalendarDateToRelativeDate(from as string),
              to && DateRange.convertCalendarDateToRelativeDate(to as string),
            ];
          } else {
            [from, to] = [
              from && DateRange.convertRelativeDateToCalendarDate(from as RelativeDate),
              to && DateRange.convertRelativeDateToCalendarDate(to as RelativeDate),
            ];
          }
          this.dispatchChangeDateRangeEvent({rolling, from, to});
        },
        canUseRolling: () => {
          return this.state.dateRange.canUseRolling();
        },
        getRollingCheckboxText: () => {
          const {t} = this.getContext(`i18n`);
          switch (this.state.dateRange.type) {
            case DateRangeType.Between:
              return t(`reports.date-range.rolling.range`);
            case DateRangeType.EndsOn:
            case DateRangeType.StartsOn:
            case DateRangeType.On:
              return t(`reports.date-range.rolling.date`);
          }

          return ``;
        },
        getRollingInfoText: () => {
          const {t} = this.getContext(`i18n`);
          switch (this.state.dateRange.type) {
            case DateRangeType.Between:
              return t(`reports.date-range.rolling.tooltips.range`);
            case DateRangeType.EndsOn:
            case DateRangeType.StartsOn:
            case DateRangeType.On:
              return t(`reports.date-range.rolling.tooltips.date`);
          }

          return ``;
        },
      },
    };
  }

  focusRelativeInput() {
    this.relativeRangeInput.focus();
  }

  selectRelativeInput() {
    this.relativeRangeInput.select();
  }

  focusFromInput() {
    if (this.initialized) {
      this.calendarInput.focusFromInput();
    }
  }

  selectFromInput() {
    if (this.initialized) {
      this.calendarInput.selectFromInput();
    }
  }

  focusToInput() {
    if (this.initialized) {
      this.calendarInput.focusToInput();
    }
  }

  selectToInput() {
    if (this.initialized) {
      this.calendarInput.selectToInput();
    }
  }

  dispatchChangeDateRangeEvent(attributes: Partial<DateRangeAttributes>) {
    const dateRange = this.state.dateRange.mergeAttributes(attributes);
    this.dispatchEvent(new CustomEvent(`changeDateRange`, {detail: {dateRange}}));
  }

  get calendarInput() {
    return this.el.querySelector(`mp-calendar-input`) as CalendarInput;
  }

  get relativeRangeInput() {
    return this.el.querySelector(`input.relative-range-value`) as HTMLInputElement;
  }

  getDisplayContext() {
    return this.attr(`display-context`) || DisplayContext.Default;
  }

  getMaxDateLabel() {
    return formatDate(parseDate(this.attr(`max-date`)));
  }
}

type JsxProps = {
  attrs?: Partial<Attrs>;
  on?: {
    draw?: (ev: CustomEvent) => void;
    changeDateRange?: (ev: CustomEvent<{dateRange: DateRange}>) => void;
    confirmDateRange?: (ev: CustomEvent) => void;
  };
};

// Note: `date-range-picker-new` has the same element name. They should be different to avoid confusion when searching the codebase
export const DateRangePickerEl = defineAndMakeJsxFactory<JsxProps>(`mp-date-range-picker`, DateRangePicker);
export const DateRangePickerReact = panel2react<JsxProps>(`mp-date-range-picker`, DateRangePicker);
